import _ from 'lodash'
import Backbone from 'backbone'
import i18n from 'scoreboard/components/i18n'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import appDisplay from 'scoreboard/components/app-display'
import EditStackLabel from './edit-stack-label'
import ColumnDataSourceForm from './column-data-source-form'
import FormattingForm from './formatting-form'
import NoteDisplayForm from './note-display-form'
import Tooltip from 'scoreboard/components/tooltip'
import reportConstants from 'scoreboard/reports/report-constants'
import unwrapModel from 'scoreboard/components/unwrap-model'

const TestDeletableOption = BootslideMenu.DeleteNode.extend({

	initialize: function(opts = {}) {
		this.options = opts
		if (!_.isNil(_.result(this.options, 'testDeletable'))) {
			// #37781 used to style this option when it is disabled
			this.$el.toggleClass('is-disabled', true)
		}
	},

	delete: function (e) {
		e.stopPropagation()
		e.preventDefault()

		let preventDeleteMessage = _.result(this.options,
			'testDeletable')
		if (_.isNil(preventDeleteMessage)) {
			this._doDelete()
		} else {
			_.invoke(this._tooltip, 'remove')
			this._tooltip = new Tooltip({
				attachTo: this.el,
				content: preventDeleteMessage
			}).render()
		}
	},

	/**
	 * The actual deletion to occur if the check passes
	 * @abstract
	 */
	_doDelete: function() {},

	autoRemove: true
})

const DeleteOption = TestDeletableOption.extend({
	_doDelete: function() {
		if (this.model.has('repeatBy')) {
			//if this is a repeat by layout remove the column from the layout
			//removing the last column will automatically remove the stack
			this.model.columns().remove(this.model.findColumn(this.options.column))
		} else {
			this.model.destroy()
		}
		//trigger the "deleted" event to close the tooltip,
		this.model.trigger('deleted')
	}
})

const DialogOption = Backbone.View.extend({

	events: {
		'click': '_click'
	},

	/**
	 * A Clickable option that launches a dialog for a given view
	 * @param {String} opts.label the label of the option in the menu
	 * @param {ReportStack} options.model the model of a Report Stack
	 * @param {ReportModel} opts.reportModel the model of the report
	 * @param {ReportTableModel} opts.tableModel the model of the table
	 * @param {ReportColumn} opts.column the model of a single column in stack
	 * @param {EditStackMenu} opts.stackMenu the menu for editing the stack
	 */
	initialize: function(opts = {}) {
		this.options = opts

		this.column = opts.column
		this.view = opts.view

		_.defaults(this.options, {
			//listen for what the dialog returns to set on the model, the default
			//actions is to set the stack's definitions meta since that is the
			//majority of the actions done in editing a stack
			onDone: dialogData => {
				if (dialogData) {
					this.column.set(_.extend({
						//default selection means attribute will not be present
						precision: dialogData.attributes.precision || null

						// #54885 the id of the column won't change so don't set
						// it as it can cause some issues
					}, _.omit(dialogData.attributes, ['id'])))
				}
				this.trigger('done')
			}
		})
		this._onDone = this.options.onDone
	},

	render: function() {
		this.$el.text(i18n.get(this.options.label))
		return this
	},

	_click: function(e) {
		e.stopPropagation()
		let { model } = this

		//editing a stack in this dialog should not cause a fetch because the
		//the fetch should only be done once the form's changes have been committed
		//and the dialog closed (which is handled by the done listener below) #40735
		let viewOptions = _.assign({noFetch: true}, this.options)
		let view = new this.view(_.defaults({model}, viewOptions))
		view.on('done', this._onDone)
		appDisplay.dialog(view)

		this.options.stackMenu.trigger('select-option')
	}

})

const EditStackMenu = BootslideMenu.extend({

	/**
	 * Bootslide menu exposing editing options for a stack
	 *
	 * @param {ReportStack} options.model the model of a Report Stack
	 * @param {ReportModel} options.reportModel the model of the report
	 * @param {ReportTableModel} options.tableModel the model of the table
	 * @param {ReportColumn} options.column the model of a single column in the stack
	 */
	initialize: function (options = {}) {
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.column = options.column
		this.reportModel = options.reportModel

		this.listenTo(this.model, 'change:advancedLayoutToggle', (m,enabled) => {
			if (!enabled) {
				//save the work done
				this.model.advancedLayoutWork = this.model.get('advancedLayout')
			}
			this.model.set('advancedLayout', enabled ? this.model.advancedLayoutWork : null)
			this.bootslide.toggleItem('data-source', !enabled)
			this.bootslide.toggleItem('formatting-form', !enabled)
			this.bootslide.toggleItem('build-advanced-layout', enabled)
		})
		this.allowDynamicWidth = true
	},

	menu: function () {
		let target = []
		let { model } = this
		let basicLayout = _.isNil(this.model.get('advancedLayout')) && this.model.get('advancedLayoutToggle') != true
		let groupingPosition = this.reportModel.groupByPosition(this.column)
		const isGroupedHeader = isColumnGroupedHeader(this.reportModel,
			groupingPosition)

		// work is mostly done, but we're delaying this feature til later
		// target.push({
		// 	label: 'report.designer.advancedLayout',
		// 	contentView: BootslideMenu.ToggleProperty.extend({
		// 		property: {
		// 			name: 'advancedLayoutToggle',
		// 			label: 'report.designer.advancedLayout'
		// 		}
		// 	})
		// })

		target.push({
			contentView: new DialogOption(_.defaults({
				stackMenu: this,
				model,
				view: EditStackLabel,
				label: 'report.designer.setColumnLabel'
			}, this.options))
		})

		target.push({
			id: 'data-source',
			contentView: new DialogOption(_.defaults({
				stackMenu: this,
				model,
				view: ColumnDataSourceForm,
				label: 'report.designer.setDataSource'
			}, this.options)),
			hidden: !basicLayout
		})

		if (this.column.get('columnType') == reportConstants.REPORT_COLUMN_NOTES_COLUMN_TYPE_ID) {
			target.push({
				id: 'note-display-form',
				contentView: new DialogOption(_.defaults({
					stackMenu: this,
					model,
					view: NoteDisplayForm,
					label: 'report.designer.setNoteDisplay'
				}, this.options)),
				hidden: !basicLayout || isGroupedHeader
			})
		}

		//noFetch is false because we're showing an example designer table to
		//show the changes to the formatting to the user as they make them
		target.push({
			id: 'formatting-form',
			contentView: new DialogOption(_.defaults({
				stackMenu: this,
				model,
				view: FormattingForm,
				label: 'report.designer.editFormatting'
			}, {noFetch: false}, this.options)),
			hidden: !basicLayout || isGroupedHeader
		})

		// work mostly done, but we're delaying this feature til later
		// target.push({
		// 	id: 'build-advanced-layout',
		// 	contentView: new DialogOption(_.defaults({
		// 		model,
		// 		defaultMeta: model.columns().at(0).attributes,
		// 		view: CellLayoutForm,
		// 		label: 'report.designer.buildAdvancedLayout',
		// 		onDone: advancedLayout => {
		// 			//update the layout on save
		// 			model.set('advancedLayout', advancedLayout)
		// 		}
		// 	}, this.options)),
		// 	hidden: basicLayout
		// })

		target.push({
			label: 'common.delete',
			contentView: new DeleteOption(this.options)
		})
		return {
			label: 'report.designer.editColumn',
			target,
			width: this.width || 180 // min-width of 180px if this.width is undefined
		}
	}

})

const isColumnGroupedHeader = (reportModel, groupingPosition) => {
	reportModel = unwrapModel(reportModel)
	const { showIndividualRecords, groupBy } = reportModel
	return (showIndividualRecords && groupingPosition == 0)
		|| (_.size(groupBy) > 1 && !showIndividualRecords && groupingPosition == 0)
		|| (groupingPosition == 1 && showIndividualRecords)
}


export default EditStackMenu
export { isColumnGroupedHeader }
export { TestDeletableOption }

EditStackMenu.__filename__ = 'reports/common/edit-stack-menu.js'
